import React from 'react';
import Card from '../../Card';
import DegradedBanner from '../DegradedBanner';

// ISSUE-014：临界短板（is_borderline=true，60-70 分）用中性蓝灰；
// 真短板按 score 配色保持原状（橙红）
const BORDERLINE_COLOR = '#64748b';

function scoreColor(score) {
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#2563eb';
  return '#d97706';
}

/**
 * V4 P4：欠缺维度（典型错题 AI 解析 + 维度总结）。
 *
 * ISSUE-014（思路 C）：dim_summaries 含两类：
 *   - is_borderline=false：真短板（score < 60），文案语气"薄弱/失分类型"
 *   - is_borderline=true：临界短板（60-70 分），UI 用中性灰边 + "临界"标签，
 *     文案温和"待提升 / 接近达标"，避免让 65 分维度被冠以"严重短板"
 *
 * 用户全维度都 ≥ 70 时 dim_summaries 为空 + questions 为空 → 走 fallback
 *
 * narrative_v4.weakness_questions = [
 *   { text, model_used, degraded, attempts,
 *     q_no, stem, user_option, correct_option, is_avoidance, dimension_id },
 *   ...
 * ]
 * narrative_v4.dim_summaries = [
 *   { text, ..., dimension_id, dimension_name, score, is_borderline },
 *   ...
 * ]
 *
 * UI 顺序：先错题逐题解析（重点），后维度总结收尾。
 */
export default function V4P4Weakness({ report }) {
  const questions = report?.narrative_v4?.weakness_questions || [];
  const dimSummaries = report?.narrative_v4?.dim_summaries || [];

  // ISSUE-014：用户全维度 ≥ 70 → 既无短板维度也无错题
  if (questions.length === 0 && dimSummaries.length === 0) {
    return (
      <div data-testid="report-v4-p4">
        <Card style={{ padding: '24px 28px' }}>
          <h2 style={{ marginTop: 0, marginBottom: 12, fontSize: 18 }}>欠缺维度分析</h2>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.8 }}>
            本次测评未识别到明显短板维度（所有维度得分均达到良好水平）。
            请直接查看下一页的"综合评价 + 提升方案"，了解进阶路径。
          </p>
        </Card>
      </div>
    );
  }

  const allBorderline = dimSummaries.length > 0 && dimSummaries.every(d => d.is_borderline);
  const headerCopy = questions.length > 0
    ? `挑选了 ${questions.length} 道典型错题逐题解析，并对${allBorderline ? '待提升' : '短板'}维度做整体总结。`
    : '本次未识别到典型错题，可直接查看维度总结。';

  return (
    <div data-testid="report-v4-p4">
      <Card style={{ padding: '20px 28px', marginBottom: 24 }}>
        <h2 style={{ marginTop: 0, marginBottom: 6, fontSize: 18 }}>
          {allBorderline ? '待提升维度分析' : '欠缺维度分析'}
        </h2>
        <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: 0 }}>
          {headerCopy}
        </p>
      </Card>

      {questions.length > 0 && (
        <Card style={{ padding: '24px 28px', marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginTop: 0, marginBottom: 16 }}>
            典型错题解析
          </h3>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 22 }}>
            {questions.map((q, i) => (
              <li
                key={`${q.q_no ?? i}-${i}`}
                data-testid={`report-v4-p4-q-${i}`}
                style={{ paddingLeft: 14, borderLeft: '3px solid #d97706' }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#9a3412', background: '#fff7ed', padding: '2px 8px', borderRadius: 10 }}>
                    第 {q.q_no ?? '—'} 题
                  </span>
                  {q.is_avoidance && (
                    <span style={{ fontSize: 11, color: '#b45309', background: '#fef3c7', padding: '2px 8px', borderRadius: 10 }}>
                      回避题
                    </span>
                  )}
                </div>
                {q.stem && (
                  <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 6px', color: 'var(--color-text)' }}>
                    {q.stem}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8, flexWrap: 'wrap' }}>
                  {!q.is_avoidance && (
                    <span>你的答案：<span style={{ color: '#b91c1c', fontWeight: 600 }}>{q.user_option || '—'}</span></span>
                  )}
                  <span>正确答案：<span style={{ color: '#16a34a', fontWeight: 600 }}>{q.correct_option || '—'}</span></span>
                </div>
                {q.degraded && <DegradedBanner note="本题 AI 解析未生成，已使用兜底文案。" />}
                <p style={{
                  fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.75,
                  whiteSpace: 'pre-wrap', margin: 0,
                }}>
                  {q.text || '（暂无解析）'}
                </p>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {dimSummaries.length > 0 && (
        <Card style={{ padding: '24px 28px', marginBottom: 24 }} data-testid="report-v4-p4-dim-summaries">
          <h3 style={{ fontSize: 15, fontWeight: 700, marginTop: 0, marginBottom: 16 }}>
            {allBorderline ? '待提升维度总结' : '短板维度总结'}
          </h3>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 18 }}>
            {dimSummaries.map((d, i) => {
              const score = d.score;
              const isBorderline = !!d.is_borderline;
              const sColor = isBorderline
                ? BORDERLINE_COLOR
                : (typeof score === 'number' ? scoreColor(score) : '#d97706');
              return (
                <li
                  key={d.dimension_id ?? `ds-${i}`}
                  data-testid={`report-v4-p4-dim-${i}`}
                  data-is-borderline={isBorderline ? 'true' : 'false'}
                  style={{ paddingLeft: 14, borderLeft: `3px solid ${sColor}` }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
                    <p style={{ fontSize: 14, fontWeight: 600, margin: 0, minWidth: 0, wordBreak: 'break-word' }}>
                      {d.dimension_name || `维度 ${i + 1}`}
                      {isBorderline && (
                        <span style={{
                          marginLeft: 8, fontSize: 11, fontWeight: 600,
                          color: BORDERLINE_COLOR, background: '#f1f5f9',
                          padding: '2px 8px', borderRadius: 10,
                          whiteSpace: 'nowrap', verticalAlign: 'middle',
                        }}>
                          临界
                        </span>
                      )}
                    </p>
                    {typeof score === 'number' && (
                      <span style={{ fontSize: 13, fontWeight: 700, color: sColor, whiteSpace: 'nowrap' }}>
                        {score} 分
                      </span>
                    )}
                  </div>
                  {d.degraded && <DegradedBanner />}
                  <p style={{
                    fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.75,
                    whiteSpace: 'pre-wrap', margin: 0,
                  }}>
                    {d.text || '（暂无内容）'}
                  </p>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
